// «Штаб» — личное досье игрока (публичная роль + скрытая цель/мотив/лояльность +
// служебная сводка + настройки аватара и пола) и состав совета. Изолированная
// .gn-* разметка; данные — setup.commandHub / myPrivateProfile / characters /
// members. Мутации идут через переданный action() (он же перезагружает setup).

import { useEffect, useRef, useState } from "react";
import {
  api,
  type CampaignSetup,
  type CharacterPublic,
  type HiddenLoyalty,
  type PersonalGoalProgress,
  type PlayerGender,
  type SetupMember,
} from "../api";
import { initials } from "../cycle/shared";

const GEN = "/assets/game-ui/gen";
type GnAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;

function clampPct(v: number): number {
  return Math.max(0, Math.min(100, Math.round(v)));
}
function membersPlural(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return "участник";
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return "участника";
  return "участников";
}
function loyaltyKind(kind: HiddenLoyalty["targetType"]): string {
  return kind === "FACTION" ? "фракция" : kind === "INSTITUTION" ? "институт" : "принцип";
}
function stageStatusLabel(status: string): string {
  return status === "COMPLETED" ? "Выполнено" : status === "ACTIVE" ? "Сейчас" : "Впереди";
}
function influenceLabel(value: number, max: number): string | null {
  if (!value || value <= 0) return null;
  const ratio = value / Math.max(1, max);
  return ratio >= 0.75 ? "высокое" : ratio >= 0.4 ? "среднее" : "скромное";
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

// Аватар участника: CUSTOM → блоб, TELEGRAM → url, иначе — инициалы.
function GnAvatar({ campaignId, member }: { campaignId: string; member: SetupMember }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const kind = member.avatar.kind;
  const stamp = member.avatar.updatedAt;
  useEffect(() => {
    setFailed(false);
    if (kind !== "CUSTOM") {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .campaignAvatarContent(campaignId, member.campaignMemberId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaignId, member.campaignMemberId, kind, stamp]);

  if (kind === "CUSTOM" && url && !failed) return <img src={url} alt="" />;
  if (kind === "TELEGRAM" && member.avatar.telegramUrl && !failed)
    return <img src={member.avatar.telegramUrl} alt="" onError={() => setFailed(true)} />;
  return <span className="gn-ini">{initials(member.displayName)}</span>;
}

function CharacterSettings({
  campaignId,
  member,
  action,
}: {
  campaignId: string;
  member: SetupMember;
  action: GnAction;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const gen = member.avatarGeneration;
  const style = member.avatar.styleStatus ?? "NONE";
  const styleText =
    style === "READY"
      ? "Комикс-аватар готов — вас рисуют похожим на фото."
      : style === "QUEUED"
        ? "Комикс-аватар создаётся…"
        : style === "FAILED"
          ? "Стилизация не удалась — попробуйте другое фото."
          : "Загрузите фото — и вас будут рисовать в сценах.";

  async function choose(file?: File) {
    if (!file) return;
    await action("Загружаем фото и ставим стилизацию в очередь…", async () => {
      if (!["image/jpeg", "image/png"].includes(file.type)) throw new Error("Только JPEG или PNG");
      if (file.size > 5 * 1024 * 1024) throw new Error("Не больше 5 МБ");
      return api.uploadCampaignAvatar(campaignId, file);
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  const genders: { value: PlayerGender; label: string }[] = [
    { value: "UNSPECIFIED", label: "Не указан" },
    { value: "MALE", label: "Мужской" },
    { value: "FEMALE", label: "Женский" },
  ];

  return (
    <>
      <div className="gn-msect gn-priv">Настройки персонажа</div>
      <div className="gn-setrow">
        <span className="gn-avwrap">
          <GnAvatar campaignId={campaignId} member={member} />
          {style === "READY" && <span className="gn-avbadge">комикс</span>}
        </span>
        <div className="gn-avctl">
          <b>Аватар</b>
          <p>{styleText}</p>
          <div className="gn-avbtns">
            <button type="button" className="gn-avbtn" onClick={() => inputRef.current?.click()}>
              {member.avatar.kind === "CUSTOM" ? "Заменить фото" : "Загрузить фото"}
            </button>
            {member.avatar.kind === "CUSTOM" && (
              <button
                type="button"
                className="gn-avbtn gn-ghost"
                onClick={() => void action("Сбрасываем фото…", () => api.resetCampaignAvatar(campaignId))}
              >
                Сбросить
              </button>
            )}
          </div>
        </div>
        <input
          ref={inputRef}
          hidden
          type="file"
          accept="image/jpeg,image/png"
          onChange={(e) => void choose(e.target.files?.[0])}
        />
      </div>
      <label className={`gn-gtoggle${gen.enabled ? " on" : ""}`}>
        <input
          type="checkbox"
          checked={gen.enabled}
          disabled={!gen.available}
          onChange={(e) =>
            void action(
              e.target.checked ? "Включаем аватар в иллюстрациях…" : "Отключаем аватар в иллюстрациях…",
              () => api.updateAvatarGeneration(campaignId, e.target.checked),
            )
          }
        />
        <span className="gn-box">✓</span>
        <span>
          <b>Рисовать меня в иллюстрациях по фото</b>
          <small>{gen.hint || "Персонаж в ваших действиях будет похож на снимок, но перерисован в стиле хроники."}</small>
        </span>
      </label>

      <div className="gn-msect">Пол персонажа</div>
      <div className="gn-genopts">
        {genders.map((g) => (
          <button
            key={g.value}
            type="button"
            className={`gn-genopt${member.playerGender === g.value ? " on" : ""}`}
            onClick={() => void action("Обновляем пол персонажа…", () => api.updatePlayerGender(g.value))}
          >
            {g.label}
          </button>
        ))}
      </div>
      <p className="gn-ghint">Влияет на род в летописи, действиях и в промптах изображений.</p>
    </>
  );
}

function GoalStages({ goal }: { goal: PersonalGoalProgress }) {
  const stages = [...goal.stages].sort((a, b) => a.order - b.order);
  return (
    <div className="gn-stages">
      {stages.map((s) => (
        <div
          key={s.id}
          className={`gn-strow ${s.status === "COMPLETED" ? "done" : s.status === "ACTIVE" ? "active" : "lock"}`}
        >
          <span className="gn-sdot" />
          <span className="gn-stx">
            <span className="gn-sk">{stageStatusLabel(s.status)}</span>
            <b>{s.title}</b>
          </span>
        </div>
      ))}
    </div>
  );
}

function DossierModal({
  campaignId,
  setup,
  me,
  onClose,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  me: SetupMember;
  onClose: () => void;
  action: GnAction;
}) {
  const hub = setup.commandHub;
  const myChar = setup.characters.find((c) => c.campaignMemberId === me.campaignMemberId);
  const priv = setup.myPrivateProfile;
  const name = hub?.character.displayName ?? myChar?.displayName ?? me.displayName;
  const office = myChar?.publicTitle ?? priv?.officeTitle ?? me.assignedOfficeTitle ?? "Участник Совета";
  const gender = hub?.character.genderLabel;
  const publicDuties = myChar?.publicDuties ?? hub?.character.dutiesFull;
  const specialPower = myChar?.specialPower ?? hub?.character.specialPowerFull;
  const goal = priv?.personalGoalProgress;
  const loyalty = priv?.hiddenLoyalty;

  return (
    <div className="gn-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="gn-modal gn-dossier" onClick={(e) => e.stopPropagation()}>
        <div className="gn-modal-head">
          <div className="gn-tt">
            <b>Полное досье</b>
            <span>Штаб · только для вас</span>
          </div>
          <button type="button" className="gn-modal-x" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="gn-dosshead">
          <span className="gn-pic">
            <GnAvatar campaignId={campaignId} member={me} />
          </span>
          <div>
            <h3>{name}</h3>
            <div className="gn-orole">{office}</div>
            <div className="gn-dsub">{[gender, "участник Совета"].filter(Boolean).join(" · ")}</div>
          </div>
        </div>

        {publicDuties && (
          <>
            <div className="gn-msect">Публичная роль</div>
            <p className="gn-mtext">{publicDuties}</p>
          </>
        )}

        {specialPower && (
          <>
            <div className="gn-msect">Особое полномочие</div>
            <div className="gn-powerline">
              <img src={`${GEN}/role_office.png`} alt="" />
              <p>{specialPower}</p>
            </div>
          </>
        )}

        {goal && (
          <>
            <div className="gn-msect gn-priv">
              Личная цель <small>· скрыта от других</small>
            </div>
            {goal.description && (
              <p className="gn-mtext" style={{ marginBottom: "6px" }}>
                {goal.description}
              </p>
            )}
            <GoalStages goal={goal} />
          </>
        )}

        {priv?.hiddenMotiveTitle && (
          <>
            <div className="gn-msect gn-priv">
              Скрытый мотив <small>· только для вас</small>
            </div>
            <p className="gn-mtext">
              <b>{priv.hiddenMotiveTitle}.</b>
              {priv.hiddenMotiveDescription ? ` ${priv.hiddenMotiveDescription}` : ""}
            </p>
          </>
        )}

        {loyalty && (
          <>
            <div className="gn-msect gn-priv">
              Скрытая лояльность <small>· {loyaltyKind(loyalty.targetType)}</small>
            </div>
            <p className="gn-mtext">{loyalty.description || loyalty.title}</p>
            {loyalty.conflictHint && <div className="gn-privnote">{loyalty.conflictHint}</div>}
          </>
        )}

        {priv?.personalIntroduction && (
          <>
            <div className="gn-msect">Вступление в хронику</div>
            <p className="gn-mtext">{priv.personalIntroduction}</p>
          </>
        )}


        <CharacterSettings campaignId={campaignId} member={me} action={action} />
        <div style={{ height: "14px" }} />
      </div>
    </div>
  );
}

function MemberModal({
  campaignId,
  setup,
  character,
  onClose,
}: {
  campaignId: string;
  setup: CampaignSetup;
  character: CharacterPublic;
  onClose: () => void;
}) {
  const member = setup.members.find((m) => m.campaignMemberId === character.campaignMemberId);
  const maxInf = Math.max(1, ...setup.characters.map((c) => c.influence));
  const inf = influenceLabel(character.influence, maxInf);

  return (
    <div className="gn-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="gn-modal gn-dossier" onClick={(e) => e.stopPropagation()}>
        <div className="gn-modal-head">
          <div className="gn-tt">
            <b>Советник</b>
            <span>Штаб · публичная карточка</span>
          </div>
          <button type="button" className="gn-modal-x" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="gn-dosshead">
          <span className="gn-pic">
            {member ? <GnAvatar campaignId={campaignId} member={member} /> : <span className="gn-ini">{initials(character.displayName)}</span>}
          </span>
          <div>
            <h3>{character.displayName}</h3>
            <div className="gn-orole">{character.publicTitle}</div>
            {inf && (
              <div className="gn-influence">
                <i />
                Влияние: <b>{inf}</b>
              </div>
            )}
          </div>
        </div>

        {character.publicDuties && (
          <>
            <div className="gn-msect">Публичные обязанности</div>
            <p className="gn-mtext">{character.publicDuties}</p>
          </>
        )}

        {character.specialPower && (
          <>
            <div className="gn-msect">Особое полномочие</div>
            <div className="gn-powerline">
              <img src={`${GEN}/role_office.png`} alt="" />
              <p>{character.specialPower}</p>
            </div>
          </>
        )}

        <div style={{ height: "10px" }} />
        <div className="gn-panel gn-dashnote">
          <p>
            Личные цели и мотивы других советников <b>скрыты</b> — узнавайте их через диалоги и действия.
          </p>
        </div>
        <div style={{ height: "8px" }} />
      </div>
    </div>
  );
}

export function HqScreen({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: GnAction;
}) {
  const [dossierOpen, setDossierOpen] = useState(false);
  const [memberCard, setMemberCard] = useState<CharacterPublic | null>(null);

  const hub = setup.commandHub;
  const me = setup.members.find((m) => m.isCurrentUser);
  const charByMember = new Map(setup.characters.map((c) => [c.campaignMemberId, c]));

  if (!me) {
    return (
      <div className="gn-stub">
        <img src={`${GEN}/nav_council.png`} alt="" />
        <h3>Штаб</h3>
        <p>Личное досье и состав совета появятся, когда вы войдёте в кампанию.</p>
      </div>
    );
  }

  const myChar = charByMember.get(me.campaignMemberId);
  const priv = setup.myPrivateProfile;
  const myName = hub?.character.displayName ?? myChar?.displayName ?? me.displayName;
  const myOffice =
    myChar?.publicTitle ??
    priv?.officeTitle ??
    me.assignedOfficeTitle ??
    (me.memberRole === "OWNER" ? "Организатор кампании" : "Участник Совета");
  const genderLabel = hub?.character.genderLabel;
  const myDuties = hub?.character.dutiesShort ?? myChar?.publicDuties ?? "";
  const goal = priv?.personalGoalProgress;
  const activeStage = goal?.stages.find((s) => s.status === "ACTIVE");
  const hasSecret = !!(priv?.hiddenMotiveTitle || priv?.hiddenLoyalty);

  const roster = [...setup.members].sort((a, b) =>
    a.isCurrentUser === b.isCurrentUser ? 0 : a.isCurrentUser ? -1 : 1,
  );

  return (
    <div className="gn-body">
      <button type="button" className="gn-panel gn-dhero" onClick={() => setDossierOpen(true)}>
        <span className="gn-dmed">
          <GnAvatar campaignId={campaignId} member={me} />
        </span>
        <span className="gn-dm">
          <span className="gn-eyebrow">Ваша роль в Совете</span>
          <h2>{myName}</h2>
          <div className="gn-orole">{[myOffice, genderLabel].filter(Boolean).join(" · ")}</div>
          {myDuties && <p>{myDuties}</p>}
          <span className="gn-more">Открыть полное досье ›</span>
        </span>
      </button>

      {goal && (
        <div className="gn-panel gn-goal">
          <span className="gn-eyebrow">Личная цель · только для вас</span>
          <div className="gn-gtitle">{goal.title}</div>
          <div className="gn-pwrap">
            <span className="gn-pbar">
              <i style={{ width: `${clampPct(goal.progressPercent)}%` }} />
            </span>
            <span className="gn-pval">{clampPct(goal.progressPercent)}%</span>
          </div>
          {goal.description && <p>{goal.description}</p>}
          {activeStage && (
            <span className="gn-stagechip">
              <i />
              Сейчас: {activeStage.title}
            </span>
          )}
        </div>
      )}

      {hasSecret && (
        <div className="gn-panel gn-secret">
          <span className="gn-eyebrow gn-eyebrow-p">Закрытые сведения</span>
          {priv?.hiddenMotiveTitle && (
            <button type="button" className="gn-srow" onClick={() => setDossierOpen(true)}>
              <span className="gn-lk">
                <LockIcon />
              </span>
              <span className="gn-st">
                <b>Скрытый мотив</b>
                <span>{priv.hiddenMotiveTitle}</span>
              </span>
              <span className="gn-chev">›</span>
            </button>
          )}
          {priv?.hiddenLoyalty && (
            <button type="button" className="gn-srow" onClick={() => setDossierOpen(true)}>
              <span className="gn-lk">
                <LockIcon />
              </span>
              <span className="gn-st">
                <b>Скрытая лояльность</b>
                <span>{priv.hiddenLoyalty.title || priv.hiddenLoyalty.targetName}</span>
              </span>
              <span className="gn-chev">›</span>
            </button>
          )}
        </div>
      )}

      <div className="gn-panel gn-roster">
        <span className="gn-eyebrow">
          Состав совета · {setup.members.length} {membersPlural(setup.members.length)}
        </span>
        {roster.map((m) => {
          const c = charByMember.get(m.campaignMemberId);
          const title =
            c?.publicTitle ??
            (m.memberRole === "OWNER" ? "Организатор кампании" : m.assignedOfficeTitle ?? "Участник Совета");
          const power = c?.specialPower;
          const clickable = m.isCurrentUser || !!c;
          return (
            <button
              key={m.campaignMemberId}
              type="button"
              className={`gn-mrow${m.isCurrentUser ? " me" : ""}`}
              disabled={!clickable}
              onClick={() => {
                if (m.isCurrentUser) setDossierOpen(true);
                else if (c) setMemberCard(c);
              }}
            >
              <span className="gn-mav">
                <GnAvatar campaignId={campaignId} member={m} />
              </span>
              <span className="gn-minfo">
                <span className="gn-mname">
                  <b>{m.displayName}</b>
                  {m.isCurrentUser && <span className="gn-badge you">вы</span>}
                  {m.memberRole === "OWNER" && <span className="gn-badge own">владелец</span>}
                </span>
                <div className="gn-off">{title}</div>
                {power && <div className="gn-pow">{power}</div>}
              </span>
              {clickable && <span className="gn-chev">›</span>}
            </button>
          );
        })}
      </div>

      {dossierOpen && (
        <DossierModal campaignId={campaignId} setup={setup} me={me} onClose={() => setDossierOpen(false)} action={action} />
      )}
      {memberCard && (
        <MemberModal campaignId={campaignId} setup={setup} character={memberCard} onClose={() => setMemberCard(null)} />
      )}
    </div>
  );
}
